import {PhaPremiumRow, PhaProtectionRow} from './pha-sheet.model';

export type PhaOptionStrategyType = 'Single Option' | 'Vertical' | 'Butterfly' | 'Two-for-One';
export type PhaOptionSection = 'premium' | 'protection';
export type PhaOptionRow = PhaPremiumRow | PhaProtectionRow;
export interface PhaStrategyLeg {
    strategyId?: string;
    /** New/incomplete structures use listed neighbors until their initial identities are resolved. */
    strategyUseAdjacent?: boolean;
    strategyFamilyId?: string;
    strategyType?: 'Vertical' | 'Butterfly' | 'Two-for-One';
    /** Anchor first, then outward: calls increase and puts decrease. */
    strategyLeg?: number;
    /** Intended strike survives an unavailable listing, as in PricingPadLeg.expectedStrike. */
    expectedStrike?: number;
    /** Gap from the preceding leg, in points (absent on the anchor). */
    strategyGap?: number;
}

export function strategyRatio(row: PhaStrategyLeg): number {
    return (row.strategyType === 'Butterfly' || row.strategyType === 'Two-for-One') && row.strategyLeg === 1 ? 2 : 1;
}

/** Shared visible/order leg ordering. Missing strikes follow all configured strikes. */
export function compareDescendingStrikes(a: {strike: number | null}, b: {strike: number | null}): number {
    const first = a.strike == null || !isFinite(a.strike) ? -Infinity : a.strike;
    const second = b.strike == null || !isFinite(b.strike) ? -Infinity : b.strike;
    return first === second ? 0 : first > second ? -1 : 1;
}

function displayedStrike(row: PhaOptionRow): number {
    const value = row.expectedStrike === undefined ? row.strike : row.expectedStrike;
    return value === null || value === undefined || !isFinite(value) ? -Infinity : value;
}

function compareStrike(a: PhaOptionRow, b: PhaOptionRow): number {
    const first = displayedStrike(a), second = displayedStrike(b);
    return first === second ? 0 : first > second ? -1 : 1;
}

/** Underlying A–Z, trades by their highest strike, and legs highest strike first. */
export function orderOptionTrades<T extends PhaOptionRow>(rows: T[]): T[] {
    const groups: T[][] = [];
    const byId: {[id: string]: T[]} = {};
    rows.forEach(row => {
        if (!row.strategyId) { groups.push([row]); return; }
        if (!byId[row.strategyId]) { groups.push(byId[row.strategyId] = []); }
        byId[row.strategyId].push(row);
    });
    groups.forEach(group => {
        const incomplete = group.some(row => displayedStrike(row) === -Infinity);
        group.sort((a, b) => (incomplete && a.strategyId ? 0 : compareStrike(a, b))
            || (a.optionType === 'Call' ? b.strategyLeg - a.strategyLeg : a.strategyLeg - b.strategyLeg));
    });
    const highest = (group: T[]) => group.reduce((best, row) => displayedStrike(row) > displayedStrike(best) ? row : best);
    groups.sort((a, b) => optionUnderlying(a[0]).toUpperCase().localeCompare(optionUnderlying(b[0]).toUpperCase())
        || compareStrike(highest(a), highest(b)));
    return ([] as T[]).concat(...groups);
}

export function optionUnderlying(row: PhaOptionRow): string {
    return 'ticker' in row ? row.ticker : row.symbol;
}

export interface PhaOptionDisplayRow<T extends PhaOptionRow> {
    row: T;
    first: boolean;
    last: boolean;
    linked: boolean;
    /** Separate strategies from their neighbors, never two standalone legs. */
    separatorBefore: boolean;
    gap: number | null;
    /** Gap ownership follows the outer leg, independent of its displayed position. */
    gapRow: T | null;
    groupHeight: number;
    alternate: boolean;
}

export function optionDisplayRows<T extends PhaOptionRow>(rows: T[], showGaps: boolean): PhaOptionDisplayRow<T>[] {
    return rows.map((row, i) => {
        const same = (other: T) => !!row.strategyId && !!other && other.strategyId === row.strategyId;
        const members = row.strategyId ? rows.filter(same) : [];
        const next = rows[i + 1];
        const previous = rows[i - 1];
        const gapRow = showGaps && same(next) && Math.abs(row.strategyLeg - next.strategyLeg) === 1
            ? row.strategyLeg > next.strategyLeg ? row : next : null;
        return {row, first: !same(previous), last: !same(next), linked: members.length > 1,
            separatorBefore: !!previous && !same(previous) && !!(row.strategyId || previous.strategyId),
            gap: gapRow && gapRow.strategyGap !== undefined ? gapRow.strategyGap : null, gapRow,
            groupHeight: members.length * 26 + (showGaps ? Math.max(0, members.length - 1) * 26 : 0),
            alternate: i % 2 === 1};
    });
}
